import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { LogOut, CreditCard, Settings, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { USER_ROUTES } from "@/routes";

export interface NotificationItem {
  id: string;
  type: "info" | "warning";
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  dismissLabel?: string;
  onDismiss?: () => void;
}

interface UserProfileDropdownProps {
  userName: string;
  isAdmin?: boolean;
  notifications?: NotificationItem[];
}

export function UserProfileDropdown({ userName, isAdmin = false, notifications }: UserProfileDropdownProps) {
  const { t } = useTranslation();
  const locale = useRouteLocale();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const currentLocale = locale || "ro";
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationCount = notifications?.length ?? 0;
  const hasAlerts = notificationCount > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleAccount = () => {
    navigate(USER_ROUTES.events(currentLocale));
    setIsOpen(false);
  };

  const handlePachete = () => {
    navigate(`/${currentLocale}/pachete`);
    setIsOpen(false);
  };

  const handleLinkStripeAccount = () => {
    navigate(USER_ROUTES.connectedAccounts(currentLocale));
    setIsOpen(false);
  };

  const handleSettings = () => {
    navigate(USER_ROUTES.settings(currentLocale));
    setIsOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 h-auto py-2 px-3 hover:bg-muted rounded-full"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white text-sm font-semibold">
              {userName.charAt(0).toUpperCase()}
            </div>
            {hasAlerts && (
              <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background"></span>
            )}
          </div>
          <div className="hidden sm:flex flex-col text-left">
            <span className="text-sm font-semibold leading-tight text-foreground">{userName}</span>
          </div>
        </div>
        <ChevronDown className={cn("h-4 w-4 transition-transform text-muted-foreground", isOpen && "rotate-180")} />
      </Button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 z-50 w-56 rounded-md border border-border bg-background shadow-lg">
          <div className="p-1">
            {/* User Info Section */}
            <div className="px-3 py-2 border-b border-border">
              <div className="text-sm font-medium text-foreground">{userName}</div>
              {isAdmin && (
                <div className="text-xs text-muted-foreground mt-1">
                  {t("common.admin")}
                </div>
              )}
            </div>

            {/* Notifications Section - Only show for non-admin users */}
            {!isAdmin && (
              <div className="border-b border-border">
                <div className="flex items-center justify-between px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
                  <span>{t("notifications.title")}</span>
                  {notificationCount > 0 && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-foreground">
                      {notificationCount}
                    </span>
                  )}
                </div>
                {notificationCount > 0 ? (
                  <div className="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto">
                    {notifications!.map((notification) => (
                      <div
                        key={notification.id}
                        className="rounded-md border border-muted/40 bg-muted/40 p-3"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "h-2.5 w-2.5 rounded-full",
                              notification.type === "warning" ? "bg-amber-500" : "bg-sky-500"
                            )}
                          />
                          <p className="text-sm font-medium text-foreground">
                            {notification.title}
                          </p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {notification.description}
                        </p>
                        {notification.actionLabel && notification.onAction && (
                          <Button
                            variant="link"
                            size="sm"
                            className="px-0 h-auto text-xs text-pink-600 hover:text-pink-700"
                            onClick={() => {
                              notification.onAction?.();
                              setIsOpen(false);
                            }}
                          >
                            {notification.actionLabel}
                          </Button>
                        )}
                        {notification.dismissLabel && notification.onDismiss && (
                          <Button
                            variant="link"
                            size="sm"
                            className="ml-3 px-0 h-auto text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              notification.onDismiss?.();
                            }}
                          >
                            {notification.dismissLabel}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 pb-3">
                    <p className="text-xs text-muted-foreground">
                      {t("notifications.empty")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Menu Items - Only show for non-admin users */}
            {!isAdmin && (
              <>
                <button
                  onClick={handleAccount}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
                    "hover:bg-muted hover:text-foreground",
                    "focus:bg-muted focus:text-foreground focus:outline-none"
                  )}
                >
                  <Settings className="h-4 w-4" />
                  <span>{t("header.account")}</span>
                </button>

                <button
                  onClick={handlePachete}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
                    "hover:bg-muted hover:text-foreground",
                    "focus:bg-muted focus:text-foreground focus:outline-none"
                  )}
                >
                  <CreditCard className="h-4 w-4" />
                  <span>{t("navigation.pachete")}</span>
                </button>

                <button
                  onClick={handleLinkStripeAccount}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
                    "hover:bg-muted hover:text-foreground",
                    "focus:bg-muted focus:text-foreground focus:outline-none"
                  )}
                >
                  <CreditCard className="h-4 w-4" />
                  <span>{t("header.stripeConnectedAccount")}</span>
                </button>

                <button
                  onClick={handleSettings}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
                    "hover:bg-muted hover:text-foreground",
                    "focus:bg-muted focus:text-foreground focus:outline-none"
                  )}
                >
                  <Settings className="h-4 w-4" />
                  <span>{t("navigation.settings")}</span>
                </button>
              </>
            )}

            <div className="border-t border-border my-1" />

            <button
              onClick={handleLogout}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm transition-colors",
                "hover:bg-muted hover:text-destructive",
                "focus:bg-muted focus:text-destructive focus:outline-none text-destructive"
              )}
            >
              <LogOut className="h-4 w-4" />
              <span>{t("common.logout")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

